import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';
import { DailyNotificationStats } from './notificationAnalyticsService';

interface PDFExportOptions {
  title: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  summary: {
    totalNotifications: number;
    totalSent: number;
    totalFailed: number;
    totalPending: number;
    overallSuccessRate: number;
    averagePerDay: number;
  };
  entityBreakdown?: Array<{
    entityType: string;
    count: number;
    successRate: number;
  }>;
  chartElementId?: string;
}

export const pdfExportService = {
  async exportAnalyticsToPDF(options: PDFExportOptions): Promise<void> {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;

    // Header
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(options.title, pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 10;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const dateRangeText = `${format(options.dateRange.from, 'MMM d, yyyy')} - ${format(options.dateRange.to, 'MMM d, yyyy')}`;
    pdf.text(dateRangeText, pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 15;

    // Summary Statistics Section
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Summary Statistics', 20, yPosition);
    yPosition += 10;

    // Draw summary boxes
    const boxWidth = (pageWidth - 50) / 2;
    const boxHeight = 25;
    const boxSpacing = 10;
    let xPosition = 20;

    // Total Notifications
    pdf.setFillColor(59, 130, 246, 0.1);
    pdf.rect(xPosition, yPosition, boxWidth, boxHeight, 'F');
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Total Notifications', xPosition + 5, yPosition + 8);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(options.summary.totalNotifications.toString(), xPosition + 5, yPosition + 18);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${options.summary.averagePerDay.toFixed(1)} per day`, xPosition + 5, yPosition + 23);

    // Success Rate
    xPosition += boxWidth + boxSpacing;
    pdf.setFillColor(16, 185, 129, 0.1);
    pdf.rect(xPosition, yPosition, boxWidth, boxHeight, 'F');
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Success Rate', xPosition + 5, yPosition + 8);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(16, 185, 129);
    pdf.text(`${options.summary.overallSuccessRate.toFixed(1)}%`, xPosition + 5, yPosition + 18);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${options.summary.totalSent} sent successfully`, xPosition + 5, yPosition + 23);

    yPosition += boxHeight + 10;
    xPosition = 20;

    // Failed
    pdf.setFillColor(239, 68, 68, 0.1);
    pdf.rect(xPosition, yPosition, boxWidth, boxHeight, 'F');
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Failed Notifications', xPosition + 5, yPosition + 8);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(239, 68, 68);
    pdf.text(options.summary.totalFailed.toString(), xPosition + 5, yPosition + 18);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const failureRate = options.summary.totalNotifications > 0 
      ? ((options.summary.totalFailed / options.summary.totalNotifications) * 100).toFixed(1)
      : '0.0';
    pdf.text(`${failureRate}% failure rate`, xPosition + 5, yPosition + 23);

    // Pending
    xPosition += boxWidth + boxSpacing;
    pdf.setFillColor(245, 158, 11, 0.1);
    pdf.rect(xPosition, yPosition, boxWidth, boxHeight, 'F');
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Pending', xPosition + 5, yPosition + 8);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(245, 158, 11);
    pdf.text(options.summary.totalPending.toString(), xPosition + 5, yPosition + 18);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text('Awaiting delivery', xPosition + 5, yPosition + 23);

    yPosition += boxHeight + 15;

    // Chart Section
    if (options.chartElementId) {
      const chartElement = document.getElementById(options.chartElementId);
      if (chartElement) {
        try {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text('Delivery Trends', 20, yPosition);
          yPosition += 10;

          const canvas = await html2canvas(chartElement, {
            scale: 2,
            backgroundColor: '#ffffff'
          });
          
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 40;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Check if we need a new page
          if (yPosition + imgHeight > pageHeight - 30) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 15;
        } catch (error) {
          console.error('Error capturing chart:', error);
        }
      }
    }

    // Entity Breakdown Section
    if (options.entityBreakdown && options.entityBreakdown.length > 0) {
      // Check if we need a new page
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Entity Type Breakdown', 20, yPosition);
      yPosition += 10;

      // Table header
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 240, 240);
      pdf.rect(20, yPosition, pageWidth - 40, 8, 'F');
      pdf.text('Entity Type', 25, yPosition + 6);
      pdf.text('Notifications', pageWidth / 2, yPosition + 6);
      pdf.text('Success Rate', pageWidth - 50, yPosition + 6);
      yPosition += 10;

      // Table rows
      pdf.setFont('helvetica', 'normal');
      options.entityBreakdown.forEach((entity, index) => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }

        if (index % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(20, yPosition - 2, pageWidth - 40, 8, 'F');
        }

        pdf.text(entity.entityType, 25, yPosition + 4);
        pdf.text(entity.count.toString(), pageWidth / 2, yPosition + 4);
        
        // Color code success rate
        if (entity.successRate >= 90) {
          pdf.setTextColor(16, 185, 129);
        } else if (entity.successRate >= 70) {
          pdf.setTextColor(245, 158, 11);
        } else {
          pdf.setTextColor(239, 68, 68);
        }
        pdf.text(`${entity.successRate.toFixed(1)}%`, pageWidth - 50, yPosition + 4);
        pdf.setTextColor(0, 0, 0);
        
        yPosition += 8;
      });
    }

    // Footer
    const footerY = pageHeight - 15;
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Generated on ${format(new Date(), 'PPP')} at ${format(new Date(), 'p')}`, 20, footerY);
    pdf.text('Notification Analytics Report', pageWidth - 20, footerY, { align: 'right' });

    // Save the PDF
    const filename = `notification-analytics-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.pdf`;
    pdf.save(filename);
  },

  async exportDataTableToPDF(
    title: string,
    headers: string[],
    data: string[][],
    summary?: Record<string, string | number>
  ): Promise<void> {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;

    // Title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Summary section if provided
    if (summary) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      Object.entries(summary).forEach(([key, value]) => {
        pdf.text(`${key}: ${value}`, 20, yPosition);
        yPosition += 6;
      });
      yPosition += 5;
    }

    // Table
    const colWidth = (pageWidth - 40) / headers.length;
    
    // Header
    pdf.setFillColor(59, 130, 246);
    pdf.rect(20, yPosition, pageWidth - 40, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    headers.forEach((header, i) => {
      pdf.text(header, 22 + (i * colWidth), yPosition + 6);
    });
    yPosition += 10;

    // Data rows
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    
    data.forEach((row, rowIndex) => {
      if (yPosition > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
      }

      if (rowIndex % 2 === 0) {
        pdf.setFillColor(245, 245, 245);
        pdf.rect(20, yPosition - 2, pageWidth - 40, 7, 'F');
      }

      row.forEach((cell, cellIndex) => {
        const text = cell.length > 30 ? cell.substring(0, 27) + '...' : cell;
        pdf.text(text, 22 + (cellIndex * colWidth), yPosition + 4);
      });
      
      yPosition += 7;
    });

    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Generated: ${format(new Date(), 'PPP p')}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

    pdf.save(`${title.toLowerCase().replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  }
};
